import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSeasons } from '@/services/seasonService';
import { useAuth } from '@/hooks/useAuth';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from 'lucide-react';

interface SeasonFilterProps {
  selectedSeasonId: string | null;
  onSeasonChange: (seasonId: string | null) => void;
}

const SeasonFilter: React.FC<SeasonFilterProps> = ({
  selectedSeasonId,
  onSeasonChange,
}) => {
  const { organizationId } = useAuth();

  const { data: seasons = [], isLoading } = useQuery({
    queryKey: ['seasons', organizationId],
    queryFn: () => getSeasons(organizationId || ''),
    enabled: !!organizationId,
  });

  // Special filter values
  const ALL_SEASONS = 'all';
  const NO_SEASON = 'none';

  const handleChange = (value: string) => {
    if (value === ALL_SEASONS) {
      onSeasonChange(null);
    } else if (value === NO_SEASON) {
      onSeasonChange(NO_SEASON);
    } else {
      onSeasonChange(value);
    }
  };

  const currentValue = selectedSeasonId === NO_SEASON 
    ? NO_SEASON 
    : selectedSeasonId || ALL_SEASONS;

  // Find active season if it exists
  const activeSeason = seasons.find(season => season.is_active);

  return (
    <Select 
      value={currentValue} 
      onValueChange={handleChange}
      disabled={isLoading}
    >
      <SelectTrigger className="min-w-[220px]">
        <SelectValue placeholder="Filter by season" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_SEASONS}>
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
            All Seasons
          </div>
        </SelectItem>
        <SelectItem value={NO_SEASON}>
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-2 text-gray-400" />
            No Season (Always Available)
          </div>
        </SelectItem>
        
        {activeSeason && (
          <SelectItem value={activeSeason.id}>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-blue-500" />
              Current: {activeSeason.name}
            </div>
          </SelectItem>
        )}
        
        {seasons
          .filter(season => !season.is_active) // Filter out active season as it's already shown
          .map((season) => (
            <SelectItem key={season.id} value={season.id}>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                {season.name}
              </div>
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
};

export default SeasonFilter; 